import type { IntlShape } from 'react-intl';
import type { InferType, TestFunction } from 'yup';
import { bool, object, ref, string } from 'yup';

import type { CountryCodeEnumType } from '@/types/regionTypes';

import {
  getTitleOptions,
  normalizeStoredTitle,
} from '@/components/FormFields/TitleField';
import {
  EMAIL_REGX,
  MAX_CHAR,
  MAX_CHAR_FIRST_NAME,
  MAX_CHAR_LAST_NAME,
  MIN_CHAR_NAME,
  PASSWORD_REGX,
} from '@/lib/forms/const';
import { formMessages } from '@/messages/forms';

import { checkIfDateIsValid } from '../dates';

export const dobSchema = (formatMessage: IntlShape['formatMessage']) => {
  const optionSchema = object({
    value: string().required(),
    label: string().required(),
  }).nullable();

  const validateDob: TestFunction = (_, context) => {
    const requiredBirthdateMsg = formatMessage(
      formMessages.incorrectBirthDateField,
    );
    const requiredBirthdateUnder18Msg = formatMessage(
      formMessages.incorrectBirthDateFieldUnder18,
    );

    const paths = ['dobDay', 'dobMonth', 'dobYear'];
    const allValuesEmpty = paths.every((path) => context.parent[path] === null);
    const allValuesSet = paths.every((path) => context.parent[path] !== null);

    const dobDayValue = context.parent['dobDay']?.value;
    const dobMonthValue = context.parent['dobMonth']?.value;
    const dobYearValue = context.parent['dobYear']?.value;

    const isValidDate = checkIfDateIsValid(
      dobDayValue,
      dobMonthValue,
      dobYearValue,
    );

    if (allValuesEmpty || (allValuesSet && isValidDate)) {
      const selectedDate = new Date(
        parseInt(dobYearValue),
        parseInt(dobMonthValue) - 1,
        parseInt(dobDayValue),
      );
      const currentDate = new Date();

      const eighteenYearsInMilliseconds = 18 * 365.25 * 24 * 60 * 60 * 1000;

      if (selectedDate.getTime() > currentDate.getTime()) {
        return context.createError({ message: requiredBirthdateMsg });
      }

      if (
        currentDate.getTime() - selectedDate.getTime() <
        eighteenYearsInMilliseconds
      ) {
        return context.createError({ message: requiredBirthdateUnder18Msg });
      }

      return true;
    }

    return context.createError({ message: requiredBirthdateMsg });
  };

  return {
    dobDay: optionSchema.test('dobDay', '', validateDob).default(null),
    dobMonth: optionSchema.test('dobMonth', '', validateDob).default(null),
    dobYear: optionSchema.test('dobYear', '', validateDob).default(null),
  };
};

export type ForgottenPasswordSchema = InferType<
  ReturnType<typeof forgottenPasswordSchema>
>;

export const forgottenPasswordSchema = (
  formatMessage: IntlShape['formatMessage'],
) => {
  const requiredMsg = formatMessage(formMessages.requiredField);

  return object({
    email: string()
      .required(requiredMsg)
      .matches(EMAIL_REGX, formatMessage(formMessages.invalidEmailAddress))
      .default(''),
    captcha: bool()
      .default(false)
      .oneOf([true], formatMessage(formMessages.requiredField)),
  });
};

export type RenewPasswordSchema = InferType<
  ReturnType<typeof renewPasswordSchema>
>;

export const renewPasswordSchema = (
  formatMessage: IntlShape['formatMessage'],
) => {
  return object({
    captcha: bool()
      .default(false)
      .oneOf([true], formatMessage(formMessages.requiredField)),
  }).shape(passwordsSchema(formatMessage));
};

export const passwordsSchema = (formatMessage: IntlShape['formatMessage']) => ({
  ...passwordSchema(formatMessage),
  ...passwordConfirmSchema(formatMessage),
});

export const passwordSchema = (
  formatMessage: IntlShape['formatMessage'],
  withRegxValidation = true,
) => {
  if (withRegxValidation) {
    return {
      password: commonPasswordSchema(formatMessage).matches(
        PASSWORD_REGX,
        formatMessage(formMessages.passwordMustContain),
      ),
    };
  }

  return {
    password: commonPasswordSchema(formatMessage),
  };
};

export const passwordConfirmSchema = (
  formatMessage: IntlShape['formatMessage'],
) => ({
  passwordConfirm: commonPasswordSchema(formatMessage).oneOf(
    [ref('password')],
    formatMessage(formMessages.passwordsMustMatch),
  ),
});

export const commonPasswordSchema = (
  formatMessage: IntlShape['formatMessage'],
) => string().required(formatMessage(formMessages.requiredField)).default('');

export const setPasswordSchema = (
  formatMessage: IntlShape['formatMessage'],
) => {
  return object({
    captcha: bool()
      .default(false)
      .oneOf([true], formatMessage(formMessages.requiredField)),
  }).shape(passwordsSchema(formatMessage));
};

export type SetPasswordFormValues = InferType<
  ReturnType<typeof setPasswordSchema>
>;

export type NameType =
  | 'first'
  | 'last'
  | 'firstKanji'
  | 'lastKanji'
  | 'firstKatakana'
  | 'lastKatakana';

export const getNameSchema = (
  formatMessage: IntlShape['formatMessage'],
  type: NameType = 'first',
  defaultValue = '',
  forceOptional = false,
) => {
  const requiredMsg = formatMessage(formMessages.requiredField);
  const maxLength = type.startsWith('first')
    ? MAX_CHAR_FIRST_NAME
    : MAX_CHAR_LAST_NAME;
  const minLength = MIN_CHAR_NAME;
  const noSpecialCharactersBeStartOrEndRegex = /^(?!^[-\s])(?!.*[-\s]$).+$/;
  const mustAppearBetweenLettersOrNumbersRegex =
    /^(?!.*[\p{Script=Han}\p{Script=Hangul}\p{Script=Hiragana}\p{Script=Katakana}])[\p{L}\p{N}]+([-\s][\p{L}\p{N}]+)*$/u;

  const noSpecialCharactersRegex = /^[\p{L}\p{N}\s-]+$/u;
  // The string cannot contain consecutive spaces or hyphens.
  // Spaces or hyphens cannot be at the start or end of the string.
  // Spaces or hyphens must appear between letters or numbers.
  const noConsecutiveSpacesOrHyphensRegex = /^(?!.*(\s{2,}|-{2,})).*$/;

  const getMaxCharMsg = (maxLength = MAX_CHAR) =>
    formatMessage(formMessages.maxChars, {
      max: maxLength,
    });
  const getMinCharMsg = (minLength = MIN_CHAR_NAME) =>
    formatMessage(formMessages.minChars, {
      min: minLength,
    });

  const isRequired = forceOptional
    ? false
    : type === 'first' ||
      type === 'last' ||
      type === 'firstKanji' ||
      type === 'lastKanji';

  let schema = string().trim();

  if (isRequired) {
    schema = schema.required(requiredMsg);
  } else {
    schema = schema.test(
      'min-length',
      getMinCharMsg(minLength),
      (value) => !value || value.length >= minLength,
    );
  }

  schema = schema
    .max(maxLength, getMaxCharMsg(maxLength))
    .test(
      'noSpecialCharactersBeStartOrEnd',
      formatMessage(formMessages.invalidNameTrimmedDelimiterRegex),
      (value) => !value || noSpecialCharactersBeStartOrEndRegex.test(value),
    )
    .test(
      'noSpecialCharacters',
      formatMessage(formMessages.invalidNameNoSpecialCharacters),
      (value) => !value || noSpecialCharactersRegex.test(value),
    )
    .test(
      'noConsecutiveSpacesOrHyphens',
      formatMessage(formMessages.invalidNameNoConsecutiveSpacesOrHyphens),
      (value) => !value || noConsecutiveSpacesOrHyphensRegex.test(value),
    );

  const isSupportedLanguageCharacters = (input: string) =>
    /^[\p{Script=Han}\p{Script=Hangul}\p{Script=Hiragana}\p{Script=Katakana}\p{Script=Cyrillic}\p{Script=Latin}]+$/u.test(
      input,
    );

  schema = schema.test(
    'validNameCharacterScriptAndDelimiter',
    formatMessage(formMessages.invalidNameTextWithDelimiterRegex),
    (value) => {
      if (!value) return true;
      if (type === 'first' || type === 'last') {
        // Kanji or Katakana did not need this
        if (!isSupportedLanguageCharacters(value)) {
          return mustAppearBetweenLettersOrNumbersRegex.test(value);
        }
      }
      return true;
    },
  );

  return schema.default(defaultValue);
};

export const getTitleSchema = (
  formatMessage: IntlShape['formatMessage'],
  countryCode?: CountryCodeEnumType,
  defaultValue?: string | null,
  forceOptional = false,
) => {
  const requiredMsg = formatMessage(formMessages.requiredField);
  const _defaultValue = normalizeStoredTitle(countryCode, defaultValue);
  const options = getTitleOptions(formatMessage, countryCode);
  const defaultOption =
    options.find((option) => option.value === _defaultValue) ?? null;

  let schema = object({
    value: string().optional(),
    label: string().optional(),
  }).nullable();

  if (!forceOptional) {
    schema = schema.required(requiredMsg);
  }

  return schema.default(defaultOption);
};
